import React, { useState, useMemo } from "react";
import {
  Button,
  Dropdown,
  Empty,
  Flex,
  Input,
  MenuProps,
  Popconfirm,
  Skeleton,
  Space,
  Badge,
  Tag,
  Table,
  TableProps,
  Tooltip,
  Typography,
} from "antd";
import {
  SearchOutlined,
  CalendarOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  MailOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { TemplateDto, Recipients } from "api/template.service";
import { organisationService } from "api/organisation.service";
import Stage from "dtos/stage.dto";
import {
  resolveStageColor,
  resolveStageIcon,
  RESOURCE_LABELS,
  STAGE_COLORS,
  StageColor,
} from "components/Stages/constants";
import { TbCircleCheck, TbCircleDashed } from "react-icons/tb";

interface TemplateListProps {
  templates: TemplateDto[];
  loading: boolean;
  orgCode?: string;
  onSelect: (template: TemplateDto) => void;
  onDelete?: (template: TemplateDto) => void;
  onDuplicate?: (template: TemplateDto) => void;
}

type MenuInfo = Parameters<NonNullable<MenuProps["onClick"]>>[0];

const { Text } = Typography;

// ─── Helpers ────────────────────────────────────────────────────────────────

type TemplateStatus = "active" | "draft";

const STATUS_META: Record<
  TemplateStatus,
  { label: string; Icon: typeof TbCircleCheck; token: (typeof STAGE_COLORS)[StageColor] }
> = {
  active: {
    label: "Active",
    Icon: TbCircleCheck,
    token: STAGE_COLORS[StageColor.GREEN],
  },
  draft: {
    label: "Draft",
    Icon: TbCircleDashed,
    token: STAGE_COLORS[StageColor.AMBER],
  },
};

const getTemplateStatus = (template: TemplateDto): TemplateStatus => {
  if (template.triggers?.length > 0) return "active";
  return "draft";
};

/** Pill-shaped status badge with icon, matching the design reference */
const StatusPill: React.FC<{ status: TemplateStatus }> = ({ status }) => {
  const { label, Icon, token } = STATUS_META[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 999,
        background: token.bg,
        border: `1px solid ${token.border}`,
        color: token.text,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: "18px",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={12} />
      {label}
    </span>
  );
};

const formatDate = (date?: string) => {
  if (!date) return "Never";
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/** Renders a single recipient string — variable token or literal email */
const RecipientTag: React.FC<{ value: string; prefix?: string }> = ({ value, prefix }) => {
  const isVariable = value.startsWith("{{") && value.endsWith("}}");
  const label = isVariable ? `${prefix ?? ""}${value.slice(2, -2).toUpperCase()}` : `${prefix ?? ""}${value}`;

  return isVariable ? (
    <Tag bordered={false} color="cyan" style={{ fontSize: 11, marginInlineEnd: 0 }}>
      {label}
    </Tag>
  ) : (
    <Tag
      style={{
        fontSize: 11,
        marginInlineEnd: 0,
        background: "#f3f4f6",
        border: "none",
        color: "#374151",
      }}
    >
      {label}
    </Tag>
  );
};

/** Renders the recipients cell — to + cc, max 2 visible with overflow tooltip */
const RecipientsCell: React.FC<{ recipients?: Recipients }> = ({ recipients }) => {
  if (!recipients)
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    );

  const toEntries = (recipients.to ?? []).map((v) => ({ value: v, prefix: "" }));
  const ccEntries = (recipients.cc ?? []).map((v) => ({ value: v, prefix: "cc: " }));
  const all = [...toEntries, ...ccEntries];

  if (all.length === 0)
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    );

  const visible = all.slice(0, 2);
  const overflow = all.slice(2);

  return (
    <Space size={4} wrap>
      {visible.map((entry, i) => (
        <RecipientTag key={i} value={entry.value} prefix={entry.prefix} />
      ))}
      {overflow.length > 0 && (
        <Tooltip title={overflow.map((e) => `${e.prefix}${e.value}`).join(", ")}>
          <Tag style={{ fontSize: 11, marginInlineEnd: 0, background: "#f3f4f6", border: "none" }}>
            +{overflow.length} more
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
};

interface TriggersCellProps {
  triggers?: string[];
  stageMap: Record<string, Stage.Dto>;
  stagesLoading: boolean;
}

/**
 * Renders each trigger as a resolved stage pill (icon + label + stage colour).
 * Falls back to a neutral grey tag for unresolved trigger strings.
 */
const TriggersCell: React.FC<TriggersCellProps> = ({ triggers, stageMap, stagesLoading }) => {
  if (!triggers?.length)
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    );

  if (stagesLoading) {
    return (
      <Space size={4}>
        {triggers.slice(0, 2).map((t) => (
          <Tag key={t} style={{ fontSize: 11, marginInlineEnd: 0, background: "#f3f4f6", border: "none" }}>
            {t}
          </Tag>
        ))}
      </Space>
    );
  }

  const resolved = triggers.map((t) => {
    const stage = stageMap[t];
    return { raw: t, stage: stage ?? null };
  });

  const visible = resolved.slice(0, 2);
  const overflow = resolved.slice(2);

  return (
    <Space size={4} wrap>
      {visible.map(({ raw, stage }) => {
        if (!stage) {
          return (
            <Tag key={raw} style={{ fontSize: 11, marginInlineEnd: 0, background: "#f3f4f6", border: "none" }}>
              {raw}
            </Tag>
          );
        }

        const colorToken = resolveStageColor(stage.color);
        const Icon = resolveStageIcon(stage.icon);

        return (
          <Tag
            key={raw}
            style={{
              fontSize: 11,
              marginInlineEnd: 0,
              background: colorToken.bg,
              borderColor: colorToken.border,
              color: colorToken.text,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Icon size={14} />
            {stage.label}
          </Tag>
        );
      })}

      {overflow.length > 0 && (
        <Tooltip title={overflow.map(({ raw, stage }) => stage?.label ?? raw).join(", ")}>
          <Tag style={{ fontSize: 11, marginInlineEnd: 0, background: "#f3f4f6", border: "none" }}>
            +{overflow.length}
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
};

/** Derives unique resources from trigger strings and renders as badges */
const ResourceCell: React.FC<{ triggers?: string[] }> = ({ triggers }) => {
  if (!triggers?.length)
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    );

  const resources = [...new Set(triggers.map((t) => t.split(":")[0]))];

  return (
    <Space size={4} wrap>
      {resources.map((r) => (
        <Tag
          key={r}
          bordered={false}
          color="blue"
          style={{ fontSize: 11, marginInlineEnd: 0, textTransform: "capitalize" }}
        >
          {RESOURCE_LABELS[r as keyof typeof RESOURCE_LABELS] ?? r}
        </Tag>
      ))}
    </Space>
  );
};

/** 3-dot actions dropdown for a row */
const RowActions: React.FC<{
  record: TemplateDto;
  onSelect: (t: TemplateDto) => void;
  onDuplicate?: (t: TemplateDto) => void;
  onDelete?: (t: TemplateDto) => void;
}> = ({ record, onSelect, onDuplicate, onDelete }) => {
  const items: MenuProps["items"] = [
    {
      key: "edit",
      label: (
        <Space>
          <EditOutlined /> Edit
        </Space>
      ),
      onClick: (info: MenuInfo) => {
        info.domEvent.stopPropagation();
        onSelect(record);
      },
    },
    ...(onDuplicate
      ? [
          {
            key: "duplicate",
            label: (
              <Space>
                <CopyOutlined /> Duplicate
              </Space>
            ),
            onClick: (info: MenuInfo) => {
              info.domEvent.stopPropagation();
              onDuplicate(record);
            },
          },
        ]
      : []),
    ...(onDelete
      ? [
          { type: "divider" as const },
          {
            key: "delete",
            danger: true,
            label: (
              <Popconfirm
                title="Delete template?"
                description="This action cannot be undone."
                onConfirm={(e) => {
                  e?.stopPropagation();
                  onDelete(record);
                }}
                onCancel={(e) => e?.stopPropagation()}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Space onClick={(e) => e.stopPropagation()}>
                  <DeleteOutlined /> Delete
                </Space>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <Button type="text" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
    </Dropdown>
  );
};

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  orgCode,
  loading,
  onSelect,
  onDelete,
  onDuplicate,
}) => {
  const [searchText, setSearchText] = useState("");
  const stages = organisationService.useGetAllStages(orgCode);

  const stageMap = useMemo<Record<string, Stage.Dto>>(() => {
    if (!stages.data) return {};
    return stages.data.reduce<Record<string, Stage.Dto>>((acc, stage) => {
      acc[`${stage.resource}:${stage.value}`] = stage;
      return acc;
    }, {});
  }, [stages.data]);

  const filteredTemplates = useMemo(() => {
    if (!searchText) return templates;
    const s = searchText.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(s) || t.subject.toLowerCase().includes(s) || t.slug.toLowerCase().includes(s),
    );
  }, [templates, searchText]);

  const columns: TableProps<TemplateDto>["columns"] = [
    {
      title: "Template",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record: TemplateDto) => (
        <Flex align="center" gap={8}>
          <Text strong>{name}</Text>
          <StatusPill status={getTemplateStatus(record)} />
        </Flex>
      ),
    },
    {
      title: "Recipients",
      key: "recipients",
      render: (_: unknown, record: TemplateDto) => <RecipientsCell recipients={record.recipients} />,
    },
    {
      title: "Resource",
      key: "resource",
      width: 120,
      render: (_: unknown, record: TemplateDto) => <ResourceCell triggers={record.triggers} />,
    },
    {
      title: "Triggers",
      key: "triggers",
      render: (_: unknown, record: TemplateDto) => (
        <TriggersCell triggers={record.triggers} stageMap={stageMap} stagesLoading={stages.isLoading} />
      ),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 120,
      defaultSortOrder: "descend",
      sorter: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (updatedAt: string) => (
        <Tooltip title={new Date(updatedAt).toLocaleString()}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {formatDate(updatedAt)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_: unknown, record: TemplateDto) => (
        <RowActions record={record} onSelect={onSelect} onDuplicate={onDuplicate} onDelete={onDelete} />
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  return (
    <Flex vertical gap={16} className="p-5">
      <Input
        placeholder="Search by name, subject or slug..."
        allowClear
        className="max-w-96"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
      />

      {filteredTemplates.length === 0 ? (
        <Empty
          description={
            <Text type="secondary">{searchText ? "No templates match your search" : "No templates yet"}</Text>
          }
          style={{ padding: "48px 0" }}
        >
          {searchText && (
            <Button type="link" onClick={() => setSearchText("")}>
              Clear search
            </Button>
          )}
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={filteredTemplates}
          rowKey="_id"
          bordered={false}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (n) => `${n} templates` }}
          onRow={(record) => ({
            onClick: () => onSelect(record),
            style: { cursor: "pointer" },
          })}
        />
      )}
    </Flex>
  );
};

export default TemplateList;
